/*
 * Tipos do wire do módulo de Canais de Alerta (FE-6a — Tela 6 §Regras e Canais).
 *
 * Espelham FIELMENTE os schemas Pydantic do backend Fase 4 (#153 —
 * `flow/api/canais.py`). Distinto de `lib/alertas/` (histórico read-only): este é o
 * CRUD de canais + o teste de canal.
 *
 * 🔴 REGRA INVIOLÁVEL — `config` é WRITE-ONLY: a credencial (`password`/
 * `webhook_url`/`token`) é cifrada no backend e NUNCA volta. Por isso `Canal`
 * (= `CanalOut`) NÃO tem `config` — só `id`/`tipo`/`nome`/`ativo`. A credencial só
 * existe no corpo de POST/PATCH (`CanalIn`/`CanalPatch`).
 *
 * Convenção snake_case: os campos copiam o nome EXATO do JSON do backend.
 */

/**
 * Tipo do canal — Literal EXATO do backend.
 *
 * `TIPOS_CANAL` é a tupla `as const` (valor lowercase + rótulo pt-BR) para a UI
 * montar o `<select>`. `as const` torna a tupla read-only e o tipo `TipoCanal`
 * derivado dela permanece sincronizado: acrescentar um valor aqui propaga ao tipo.
 */
export const TIPOS_CANAL = [
  { valor: "smtp", rotulo: "SMTP" },
  { valor: "teams", rotulo: "Teams" },
  { valor: "slack", rotulo: "Slack" },
  { valor: "whatsapp", rotulo: "WhatsApp" },
] as const;

export type TipoCanal = (typeof TIPOS_CANAL)[number]["valor"];

/**
 * Canal de notificação (`GET /api/canais`, `.../{id}`).
 *
 * SEM `config`: o backend NUNCA serializa a credencial cifrada de volta. Leitura
 * exige a ação `configurar_alertas` (403 senão).
 */
export interface Canal {
  id: string;
  tipo: TipoCanal;
  nome: string;
  ativo: boolean;
}

/* ───────────────────────── Configs por tipo (write-only) ───────────────────────── */

/**
 * Config do canal SMTP (write-only). `password` 🔒 é a credencial — só vai no corpo
 * de POST/PATCH (HTTPS), nunca volta no `Out`.
 */
export interface ConfigSmtp {
  host: string;
  port: number;
  username: string;
  password: string;
  remetente: string;
  destinatarios: string[];
}

/**
 * Config de webhook (Teams/Slack, write-only). `webhook_url` 🔒 é a credencial
 * (deve ser `https://`) — só vai no corpo, nunca volta.
 */
export interface ConfigWebhook {
  webhook_url: string;
}

/**
 * Config do canal WhatsApp Cloud API (write-only). `token` 🔒 é a credencial — só
 * vai no corpo, nunca volta. `language_code`/`api_version` têm default no form.
 */
export interface ConfigWhatsApp {
  token: string;
  phone_number_id: string;
  destinatarios: string[];
  template_name: string;
  language_code: string;
  api_version: string;
}

/** União das configs possíveis (o backend valida pelo `tipo`). */
export type ConfigCanal = ConfigSmtp | ConfigWebhook | ConfigWhatsApp;

/**
 * Corpo do `POST /api/canais` (201).
 *
 * `config` é WRITE-ONLY e completo na criação (a credencial é obrigatória por
 * tipo). `tipo` define qual shape de `config` o backend espera.
 */
export interface CanalIn {
  tipo: TipoCanal;
  nome: string;
  config: ConfigCanal;
  ativo: boolean;
}

/**
 * Corpo do `PATCH /api/canais/{id}` (campos parciais).
 *
 * `tipo` é IMUTÁVEL (ausente aqui). `config` AUSENTE = "não trocar a credencial"
 * (mantém a cifrada atual); presente = re-cifra. WRITE-ONLY.
 */
export interface CanalPatch {
  nome?: string;
  config?: ConfigCanal;
  ativo?: boolean;
}

/**
 * Resultado do `POST /api/canais/{id}/testar` (`TesteCanalOut`).
 *
 * Falha de ENTREGA = 200 com `ok: false` (não erro HTTP). `detalhe` pode trazer
 * metadados de diagnóstico (NUNCA renderizar cru — pode conter dado sensível);
 * `erro` é a mensagem categórica segura para exibir.
 */
export interface TesteCanal {
  ok: boolean;
  detalhe: Record<string, unknown> | null;
  erro: string | null;
}

/* ─────────────────────────────── Regras (FE-6b) ─────────────────────────────── */

/*
 * Tipos do wire das REGRAS de alerta (FE-6b — Tela 6 §Regras). Espelham FIELMENTE
 * os schemas Pydantic do backend Fase 4 (#153 — `flow/api/regras.py` /
 * `schemas_alertas.py`): `RegraOut`/`RegraIn`/`RegraPatch`.
 *
 * 🔴 RIGOR FINANCEIRO: `limite` é `NUMERIC(18,2)` no banco — chega/vai como STRING
 * decimal no wire e NUNCA é convertido para `number` (exibição via `formatarDecimal`,
 * entrada via `normalizarDecimalEntrada` — ambos string-based). `canais` é a lista
 * de IDS de canal (string[]), não os objetos `Canal`.
 *
 * Convenção snake_case: os campos copiam o nome EXATO do JSON do backend.
 */

/**
 * Tipo de saldo da regra — Literal EXATO do backend. `TIPOS_SALDO` é a tupla
 * `as const` (valor + rótulo pt-BR) para a UI montar o `<select>`; o tipo
 * `TipoSaldo` deriva dela e permanece sincronizado.
 */
export const TIPOS_SALDO = [
  { valor: "realizado", rotulo: "Realizado" },
  { valor: "projetado", rotulo: "Projetado" },
] as const;

export type TipoSaldo = (typeof TIPOS_SALDO)[number]["valor"];

/**
 * Escopo da regra — Literal EXATO do backend. `grupo` = consolidado do grupo
 * (`empresa_id === null`); `empresa` = uma empresa; `conta` = uma conta de uma
 * empresa (exige `empresa_id` + `conta_id`).
 */
export const ESCOPOS = [
  { valor: "conta", rotulo: "Conta" },
  { valor: "empresa", rotulo: "Empresa" },
  { valor: "grupo", rotulo: "Grupo" },
] as const;

export type EscopoRegra = (typeof ESCOPOS)[number]["valor"];

/**
 * Severidade da regra — Literal EXATO do backend (StrEnum `Severidade`). Tupla
 * `as const` de pares valor + rótulo pt-BR.
 */
export const SEVERIDADES = [
  { valor: "informativa", rotulo: "Informativa" },
  { valor: "baixa", rotulo: "Baixa" },
  { valor: "media", rotulo: "Média" },
  { valor: "alta", rotulo: "Alta" },
  { valor: "critica", rotulo: "Crítica" },
] as const;

export type SeveridadeRegra = (typeof SEVERIDADES)[number]["valor"];

/** Limites da janela de dedup (horas) — `1..168` no backend. */
export const JANELA_DEDUP_MIN = 1;
export const JANELA_DEDUP_MAX = 168;

/** Mínimo da janela de avaliação (dias) — `≥1` no backend. */
export const JANELA_DIAS_MIN = 1;

/**
 * Regra de alerta (`GET /api/regras`, `.../{id}` — `RegraOut`).
 *
 * `limite` é STRING money (ver nota de rigor acima). `canais` é a lista de IDS de
 * canal. `empresa_id === null` => escopo de GRUPO. `conta_id` só é não-nulo no
 * escopo de conta.
 */
export interface Regra {
  id: string;
  nome: string;
  tipo_saldo: TipoSaldo;
  escopo: EscopoRegra;
  limite: string;
  severidade: SeveridadeRegra;
  canais: string[];
  janela_dias: number;
  janela_dedup_horas: number;
  empresa_id: string | null;
  conta_id: string | null;
  ativa: boolean;
  criada_em: string;
  atualizado_em: string;
}

/**
 * Corpo do `POST /api/regras` (201 — `RegraIn`).
 *
 * O backend valida as combinações cruzadas (escopo=conta exige conta_id +
 * empresa_id; conta_id só com escopo=conta; D19: projetado+conta proibido; conta
 * deve pertencer à empresa). A UI previne o óbvio; o backend é a autoridade.
 */
export interface RegraIn {
  nome: string;
  tipo_saldo: TipoSaldo;
  escopo: EscopoRegra;
  limite: string;
  severidade: SeveridadeRegra;
  canais: string[];
  janela_dias: number;
  janela_dedup_horas: number;
  empresa_id: string | null;
  conta_id: string | null;
  ativa: boolean;
}

/**
 * Corpo do `PATCH /api/regras/{id}` (campos parciais — `RegraPatch`).
 *
 * 🔴 `empresa_id` é IMUTÁVEL (ausente aqui): o backend não aceita trocar a empresa
 * de uma regra. Os demais campos são opcionais.
 */
export interface RegraPatch {
  nome?: string;
  tipo_saldo?: TipoSaldo;
  escopo?: EscopoRegra;
  limite?: string;
  severidade?: SeveridadeRegra;
  canais?: string[];
  janela_dias?: number;
  janela_dedup_horas?: number;
  conta_id?: string | null;
  ativa?: boolean;
}
